import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios, { readProfileUser } from '../utils/api';
import './Auth.css';

// Telegram Login Widget типы
declare global {
  interface Window {
    TelegramLoginWidgetRegister: {
      dataOnauth: (user: TelegramUser) => void;
    };
  }
}

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const telegramContainerRef = useRef<HTMLDivElement | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [telegramBotUsername, setTelegramBotUsername] = useState('');

  // Редирект если уже авторизован
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.get('/api/auth/profile')
        .then((response) => {
          if (readProfileUser(response.data)) navigate('/dashboard', { replace: true });
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        });
    }
  }, [navigate]);

  // Загружаем конфигурацию Telegram
  useEffect(() => {
    const loadTelegramConfig = async () => {
      try {
        const response = await axios.get('/auth/oauth/telegram/config');
        if (response.data.success && response.data.data.bot_username) {
          setTelegramBotUsername(response.data.data.bot_username);
        }
      } catch (err) {
        console.log('Telegram OAuth не настроен');
      }
    };
    loadTelegramConfig();
  }, []);

  // Telegram widget injector
  useEffect(() => {
    if (!telegramBotUsername) return;
    const container = telegramContainerRef.current;
    if (!container) return;

    container.innerHTML = '';
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', telegramBotUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-onauth', 'TelegramLoginWidgetRegister.dataOnauth(user)');
    script.setAttribute('data-request-access', 'write');
    container.appendChild(script);

    return () => { container.innerHTML = ''; };
  }, [telegramBotUsername]);

  // Telegram Login Widget callback
  useEffect(() => {
    window.TelegramLoginWidgetRegister = {
      dataOnauth: async (user: TelegramUser) => {
        setLoading(true);
        setError('');
        try {
          const response = await axios.post('/auth/oauth/telegram', user);
          if (response.data.success) {
            localStorage.setItem('token', response.data.data.tokens.accessToken);
            localStorage.setItem('refreshToken', response.data.data.tokens.refreshToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.tokens.accessToken}`;
            
            setSuccess('Успешная регистрация через Telegram!');
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 500);
          }
        } catch (err: any) {
          setError(err.response?.data?.error || 'Ошибка регистрации через Telegram');
        } finally {
          setLoading(false);
        }
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (formData.password !== formData.confirmPassword) {
      setError('Пароли не совпадают');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('/api/auth/register', {
        email: formData.email,
        password: formData.password
      });
      
      // Проверяем нужно ли подтверждение email
      if (response.data.data?.requires_email_verification) {
        setSuccess('Регистрация успешна! Проверьте вашу почту для подтверждения email.');
      } else {
        setSuccess('Регистрация успешна! Теперь вы можете войти.');
      }
      
      setFormData({
        email: '',
        password: '',
        confirmPassword: ''
      });
    } catch (err: any) {
      const apiError = err.response?.data?.message || err.response?.data?.error;
      setError(apiError || 'Ошибка регистрации');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = '/auth/oauth/google';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="container">
      <div className="auth-container">
        <div className="auth-card">
          <h2>Регистрация</h2>
          <p>Создайте аккаунт для доступа к генератору</p>

          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}

          {/* OAuth кнопки */}
          <div className="oauth-buttons">
            <button
              type="button"
              className="btn btn-oauth btn-google"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Регистрация через Google
            </button>

            <div
              className={`telegram-login-wrapper ${telegramBotUsername ? '' : 'disabled'}`}
              ref={telegramContainerRef}
              style={{ marginTop: 8, opacity: telegramBotUsername ? 1 : 0.6 }}
            >
              {!telegramBotUsername && 'Telegram недоступен'}
            </div>
          </div>

          <div className="divider">
            <span>или</span>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Пароль</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-control"
                required
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Подтвердите пароль</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Регистрация...' : 'Зарегистрироваться'}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              Уже есть аккаунт? <Link to="/login">Войти</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
